import produce from 'immer';
import React, {useMemo, useCallback} from 'react';

import {
  Node,
  NodeOrVoidNode,
  constFunction,
  listObjectType,
  opDict,
  opGroupGroupKey,
  opGroupby,
  opIndex,
  opMap,
  varNode,
  voidNode,
} from '@wandb/cg';

import * as Panel2 from './panel';
import {
  ChildPanelConfig,
  ChildPanel,
  ChildPanelConfigComp,
  ChildPanelFullConfig,
} from './ChildPanel';
import * as ConfigPanel from './ConfigPanel';
import {PanelContextProvider} from './PanelContext';
import {PANEL_LAYOUT_FLOW_DEFAULT_CONFIG} from './PanelLayoutFlow';

interface PanelEachConfig {
  section: NodeOrVoidNode;
  panel: NodeOrVoidNode;
  select: ChildPanelConfig;
  layout: ChildPanelFullConfig;
}

const PANEL_EACH_DEFAULT_CONFIG: PanelEachConfig = {
  section: voidNode(),
  panel: voidNode(),
  select: {
    id: 'Auto',
    input_node: varNode('any', 'item'),
    config: undefined,
    vars: {},
  },
  layout: {
    id: 'LayoutFlow',
    input_node: varNode('any', 'input'),
    config: undefined,
    vars: {},
  },
};

const inputType = {type: 'list' as const, objectType: 'any' as const};

type PanelEachProps = Panel2.PanelProps<typeof inputType, PanelEachConfig>;

const usePanelEachCommon = (props: PanelEachProps) => {
  const {updateConfig2} = props;
  if (updateConfig2 == null) {
    throw new Error('PanelEach requires updateConfig2');
  }
  const updateSectionExpr = useCallback(
    newExpr =>
      updateConfig2(oldConfig => {
        oldConfig = oldConfig ?? PANEL_EACH_DEFAULT_CONFIG;
        const newConfig = produce(oldConfig, draft => {
          const oldExpr = oldConfig.section;
          console.log('OLD CONFIG', oldConfig);
          console.log('OLD NEW EXPR', oldExpr, newExpr);
          draft.section = newExpr;
          // Clear config if we enabled or disabled sections.
          if (oldExpr.nodeType === 'void' && newExpr.nodeType !== 'void') {
            // sections enabled
            draft.layout.config = undefined;
            draft.layout.id = 'LayoutBank';
            draft.layout.input_node = varNode('any', 'sections');
          } else if (
            oldExpr.nodeType !== 'void' &&
            newExpr.nodeType === 'void'
          ) {
            draft.layout.config = undefined;
            draft.layout.id = 'LayoutFlow';
            draft.layout.input_node = varNode('any', 'input');
          }
        });
        console.log('NEW CONFIG', newConfig);
        return newConfig;
      }),

    [updateConfig2]
  );
  const updateChildPanelConfig = useCallback(
    newItemConfig =>
      updateConfig2(oldConfig => {
        oldConfig = oldConfig ?? PANEL_EACH_DEFAULT_CONFIG;
        console.log('NEW ITEM CONFIG', newItemConfig);
        return {
          ...oldConfig,
          select: newItemConfig,
        };
      }),

    [updateConfig2]
  );
  const updateLayoutConfig = useCallback(
    newItemConfig =>
      updateConfig2(oldConfig => {
        oldConfig = oldConfig ?? PANEL_EACH_DEFAULT_CONFIG;
        return {
          ...oldConfig,
          layout: newItemConfig, // Don't splat with ...config.item! ChildPanel always sends full config, and sometimes restructures its shape
        };
      }),

    [updateConfig2]
  );
  const updateLayoutConfig2 = useCallback(
    (change: (oldConfig: any) => any) => {
      updateConfig2(oldConfig => {
        oldConfig = oldConfig ?? PANEL_EACH_DEFAULT_CONFIG;
        const newItemConfig = change(oldConfig.layout);
        return {
          ...oldConfig,
          layout: newItemConfig,
        };
      });
    },
    [updateConfig2]
  );
  return useMemo(
    () => ({
      updateSectionExpr,
      updateChildPanelConfig,
      updateConfig2,
      updateLayoutConfig,
      updateLayoutConfig2,
    }),
    [
      updateSectionExpr,
      updateChildPanelConfig,
      updateConfig2,
      updateLayoutConfig,
      updateLayoutConfig2,
    ]
  );
};

export const PanelEachConfigComp: React.FC<PanelEachProps> = props => {
  const config = props.config ?? PANEL_EACH_DEFAULT_CONFIG;
  const {updateChildPanelConfig, updateSectionExpr} = usePanelEachCommon(props);

  const sectionVars = useMemo(
    () => ({
      row: opIndex({
        arr: props.input,
        index: varNode(listObjectType(props.input.type), 'row'),
      }),
    }),
    [props.input]
  );

  const selectVars = useMemo(
    () => ({
      item: opIndex({
        arr: props.input,
        index: varNode('number', 'index'),
      }),
    }),
    [props.input]
  );

  return (
    <div style={{height: '100%'}}>
      <ConfigPanel.ConfigOption label={'section'}>
        <PanelContextProvider newVars={sectionVars}>
          <ConfigPanel.ExpressionConfigField
            expr={config.section}
            setExpression={updateSectionExpr}
          />
        </PanelContextProvider>
      </ConfigPanel.ConfigOption>
      <PanelContextProvider newVars={selectVars}>
        <ChildPanelConfigComp
          pathEl="select"
          config={config.select}
          updateConfig={updateChildPanelConfig}
        />
      </PanelContextProvider>
    </div>
  );
};

const PanelEachFlow: React.FC<PanelEachProps> = props => {
  const config = props.config ?? PANEL_EACH_DEFAULT_CONFIG;
  const {updateLayoutConfig, updateLayoutConfig2} = usePanelEachCommon(props);

  // Map select config into child config.
  const child = useMemo(() => {
    const layout = config.layout;
    const layoutConfig = layout.config ?? PANEL_LAYOUT_FLOW_DEFAULT_CONFIG;
    return {
      ...config.layout,
      input_node: varNode(listObjectType(props.input.type), 'input'),
      config: {
        ...layoutConfig,
        panel: config.select,
      },
    };
  }, [config.layout, config.select, props.input.type]);

  return (
    <ChildPanel
      config={child}
      // TODO: need to map these into select config
      updateConfig={updateLayoutConfig}
      updateConfig2={updateLayoutConfig2}
    />
  );
};

const PanelEachBank: React.FC<PanelEachProps> = props => {
  const config = props.config ?? PANEL_EACH_DEFAULT_CONFIG;
  if (config.section.nodeType === 'void') {
    throw new Error('PanelEachBank: section must be defined');
  }
  const {updateLayoutConfig, updateLayoutConfig2} = usePanelEachCommon(props);
  // const rowType = CG.listObjectType(props.input.type);

  // const select = getFullChildPanel(config.select);

  // Steps
  const resultNode = useMemo(() => {
    const grouped = opGroupby({
      arr: props.input,
      groupByFn: constFunction(
        {row: listObjectType(props.input.type)},
        // We checked above that section is not void
        row => config.section as Node
      ),
    });
    return opMap({
      arr: grouped,
      mapFn: constFunction(
        {
          row: listObjectType(grouped.type),
          index: 'number',
        },
        ({row, index}) =>
          opDict({
            name: opGroupGroupKey({obj: row}),
            panels: row,
          } as any)
      ),
    });
  }, [config.section, props.input]);
  // const resultNodeUse = useNodeValue(resultNode);
  // console.log('RESULT NODE USE', resultNodeUse);
  // Add index column
  // GroupBy
  // map over constructing panels

  // Why do I need to construct panels here? Instead of letting PanelBank
  // ultimately? Well, for one this would work for columns, with pick instead.

  console.log('RESULT NODE TYPE', resultNode.type);

  // return (
  //   <PanelContextProvider newVars={{items: resultNode}}>
  //     <ChildPanel
  //       config={layoutConfig}
  //       updateConfig={updateLayoutConfig}
  //       updateConfig2={updateLayoutConfig2}
  //     />
  //   </PanelContextProvider>
  // );
  // Map select config into child config.
  const child = useMemo(() => {
    const layout = config.layout;
    // TODO: this should be PANEL_LAYOUT_SECTIONS_DEFAULT
    const layoutConfig = layout.config ?? PANEL_LAYOUT_FLOW_DEFAULT_CONFIG;
    return {
      ...config.layout,
      input_node: varNode(resultNode.type, 'sections'),
      config: {
        ...layoutConfig,
        panel: config.select,
      },
    };
  }, [config.layout, config.select, resultNode.type]);

  return (
    <PanelContextProvider newVars={{sections: resultNode}}>
      <ChildPanel
        config={child}
        // TODO: need to map these into select config
        updateConfig={updateLayoutConfig}
        updateConfig2={updateLayoutConfig2}
      />
    </PanelContextProvider>
  );
};

export const PanelEach: React.FC<PanelEachProps> = props => {
  const config = props.config ?? PANEL_EACH_DEFAULT_CONFIG;
  if (config.section.nodeType !== 'void') {
    return <PanelEachBank {...props} />;
  }
  return <PanelEachFlow {...props} />;
};

export const Spec: Panel2.PanelSpec = {
  id: 'Each',
  ConfigComponent: PanelEachConfigComp,
  Component: PanelEach,
  inputType,
  hidden: true,
};
